import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import categoryService from "../../repository/categoryRepository";

const CategoryModal = ({ open, onClose, onSave, initialValues = {} }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // console.log('Initial Values Received:', initialValues);

  const [categoryName, setCategoryName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState(null);
  const [iconName, setIconName] = useState("");
  const [mainCategoryId, setMainCategoryId] = useState("");
  const [mainCategoryName, setMainCategoryName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    setCategoryName(initialValues.categoryName || "");
    setDescription(initialValues.description || "");
    setMainCategoryId(initialValues.mainCategoryId || "");
    setMainCategoryName(initialValues.mainCategoryName || "");
    setCategoryId(initialValues.categoryId || "");
    setIcon(null);
    setIconName("");
  }, [initialValues]);

  const handleIconUpload = (event) => {
    const file = event.target.files[0];
    setIcon(file);
    setIconName(file ? file.name : "");
  };

  const handleSaveChanges = async () => {
    try {
      const formData = new FormData();
      formData.append("categoryName", categoryName);
      formData.append("description", description);
      formData.append("mainCategoryId", mainCategoryId);
      formData.append("mainCategoryName", mainCategoryName);
      formData.append("categoryId", categoryId);

      if (icon) {
        formData.append("imageURL", icon);
        formData.append("isImage", true);
      }

      console.log("Form Data to be sent:", formData);
      const response = await categoryService.editCategory(formData);

      console.log("Edit Category Response:", response);

      if (response.success) {
        setSnackbarMessage("Category updated successfully!");
        setSnackbarSeverity("success");
        if (onSave && typeof onSave === "function") {
          onSave(response.data);
        }
        onClose();
      } else {
        setSnackbarMessage(
          response.status === 400
            ? "Please try again."
            : "Error saving category.",
        );
        setSnackbarSeverity(response.status === 400 ? "warning" : "error");
      }
    } catch (error) {
      setSnackbarMessage("Error saving category: " + error.message);
      setSnackbarSeverity("error");
    } finally {
      setOpenSnackbar(true);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor:
              colors.background || theme.palette.background.default,
            color: colors.text || theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.primary || theme.palette.primary.main,
            color:
              theme.palette.mode === "light"
                ? "black"
                : colors.light || theme.palette.grey[100],
            position: "relative",
          }}
        >
          Edit Category
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: colors.grey || theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            type="text"
            fullWidth
            variant="outlined"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            sx={{
              marginBottom: 2,
              input: { color: colors.text || theme.palette.text.primary },
              "& .MuiInputLabel-root": {
                color: colors.text || theme.palette.text.primary,
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.text || theme.palette.text.primary,
                },
                "&:hover fieldset": {
                  borderColor: colors.primary || theme.palette.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary || theme.palette.primary.main,
                },
              },
            }}
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{
              marginBottom: 2,
              input: { color: colors.text || theme.palette.text.primary },
              "& .MuiInputLabel-root": {
                color: colors.text || theme.palette.text.primary,
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.text || theme.palette.text.primary,
                },
                "&:hover fieldset": {
                  borderColor: colors.primary || theme.palette.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary || theme.palette.primary.main,
                },
              },
            }}
          />
          <Box mb={2}>
            <Button
              variant="contained"
              component="label"
              color="secondary"
              startIcon={<CloudUploadIcon />}
              sx={{
                mb: "10px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? colors.secondary || theme.palette.secondary.main
                    : colors.secondaryLight || theme.palette.secondary.light,
                color: "black",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? colors.secondary || theme.palette.secondary.main
                      : colors.secondaryLight || theme.palette.secondary.light,
                },
              }}
            >
              Upload Icon
              <input
                type="file"
                name="icon"
                hidden
                onChange={handleIconUpload}
              />
            </Button>
            {iconName && (
              <Typography
                variant="body2"
                color={(theme) => theme.palette.text.primary}
              >
                Uploaded: {iconName}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSaveChanges}
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? colors.greenAccent || theme.palette.success.main
                  : colors.blueAccent || theme.palette.primary.main,
              color: "black",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? colors.greenAccent || theme.palette.success.main
                    : colors.blueAccent || theme.palette.primary.main,
              },
            }}
          >
            Save Category
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{
          margin: 2,
          backgroundColor:
            theme.palette.mode === "light"
              ? colors.snackbarLightBackground
              : colors.snackbarDarkBackground,
        }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{
            backgroundColor:
              snackbarSeverity === "success"
                ? colors.greenAccent
                : colors.redAccent || theme.palette.error.main,
            color:
              theme.palette.mode === "light"
                ? colors.snackbarLightText
                : colors.light || theme.palette.grey[100],
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CategoryModal;
